import { Injectable } from '@angular/core';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { ConsentService } from '@omni/services/consent/consent.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { IoConfigurationFeatureCodes, FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { Contact } from '@omni/classes/contact/contact.class';

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
const TIMESTAMP_THRESHOLD = 1000000000000; // Used to distinguish milliseconds from seconds timestamps

export interface SuggestionItem {
  contactId: string;
  contact: Contact;
  name: string;
  image?: string | Promise<string | null>;
  type: 'Expiring Consent' | 'Not Interacted';
  description: string;
  daysLeft?: number;
  timestamp: number; // For sorting when multiple suggestions for same contact
}

@Injectable({
  providedIn: 'root'
})
export class SuggestionsService {
  constructor(
    private readonly contactService: ContactOfflineService,
    private readonly consentService: ConsentService,
    private readonly authService: AuthenticationService,
    private readonly translate: TranslateService
  ) {}

  async loadSuggestions(): Promise<SuggestionItem[]> {
    const [notInteractedSuggestions, expiringConsentSuggestions] = await Promise.all([
      this.getNotInteractedSuggestions(),
      // Only get expiring consent suggestions if CONSENT_TOOL feature is enabled
      this.authService.hasFeatureAction(FeatureActionsMap.CONSENT_TOOL) ? this.getExpiringConsentSuggestions() : Promise.resolve([])
    ]);

    return [...notInteractedSuggestions, ...expiringConsentSuggestions]
      .sort((a, b) => a.name.localeCompare(b.name)); // Alphabetical order
  }

  private async getNotInteractedSuggestions(): Promise<SuggestionItem[]> {
    const notInteractedDays = this.getNotInteractedDays();
    const cutoffTimestamp = this.calculateCutoffTimestamp(notInteractedDays);

    const suggestions: SuggestionItem[] = [];
    // Ensure interaction-by-days master data is loaded (stored in local DB, overwrites on each fetch).
    await this.contactService.syncContactInteractionsByDays?.(true);

    this.contactService.contacts.forEach(contact => {
      // Align with "MY_CUSTOMERS" (Contact tool uses nonGuestContacts for that count)
      if (contact?.isguest || !contact?.isActive || contact?.indskr_iseventparticipant == true) return;
      const interactionTimestamp = this.contactService.getContactInteractionDateByDays?.(contact.ID) ?? 0;
      if (this.shouldIncludeNotInteractedContact(interactionTimestamp, cutoffTimestamp)) {
        suggestions.push(this.createNotInteractedSuggestion(contact, interactionTimestamp));
      }
    });

    return suggestions;
  }

  private getNotInteractedDays(): number {
    const configValue: string | boolean = this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.CONTACT_NOTINTERACTED_DAY_VALUE);
    return configValue ? parseInt(String(configValue), 10) : 15;
  }

  private calculateCutoffTimestamp(daysAgo: number): number {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
    return cutoffDate.getTime();
  }

  private parseInteractionDate(contact: Contact): number {
    const interactionDate: number | string | Date | undefined = contact.interactionDate ?? contact['interactionDate'];
    
    if (interactionDate === undefined || interactionDate === null) {
      return 0;
    }

    if (typeof interactionDate === 'number') {
      return interactionDate;
    }

    if (typeof interactionDate === 'string') {
      return this.parseStringInteractionDate(interactionDate);
    }

    return 0;
  }

  private parseStringInteractionDate(interactionDate: string): number {
    const numericValue = parseFloat(interactionDate);
    if (!isNaN(numericValue)) {
      return numericValue > TIMESTAMP_THRESHOLD ? numericValue : new Date(interactionDate).getTime();
    }

    const dateValue = new Date(interactionDate);
    return !isNaN(dateValue.getTime()) ? dateValue.getTime() : 0;
  }

  private shouldIncludeNotInteractedContact(interactionTimestamp: number, cutoffTimestamp: number): boolean {
    return interactionTimestamp === 0 || interactionTimestamp < cutoffTimestamp;
  }

  private createNotInteractedSuggestion(contact: Contact, interactionTimestamp: number): SuggestionItem {
    return {
      contactId: contact.ID,
      contact,
      name: contact.fullname || '',
      type: 'Not Interacted' as const,
      description: this.translate.instant('SPOTLIGHT_NO_RECENT_INTERACTIONS'),
      timestamp: interactionTimestamp || (contact.modifiedOn ? contact.modifiedOn : Date.now())
    };
  }

  private async getExpiringConsentSuggestions(): Promise<SuggestionItem[]> {
    // Only return expiring consent suggestions if CONSENT_TOOL feature is enabled
    if (!this.authService.hasFeatureAction(FeatureActionsMap.CONSENT_TOOL)) {
      return [];
    }

    const suggestions: SuggestionItem[] = [];
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const thirtyDaysTimestamp = thirtyDaysFromNow.getTime();
    const now = Date.now();

    // On relaunch, the consent subject can be empty until disk hydration / sync completes.
    await this.consentService.ensureActiveConsentsLoadedFromDisk?.();

    const activeConsents = this.consentService.allActiveConsentSubject.value || [];

    // First, collect all expiring consent suggestions per contact
    const contactSuggestionsMap = new Map<string, SuggestionItem[]>();

    activeConsents.forEach(contactConsent => {
      if (!contactConsent.activeConsents || contactConsent.activeConsents.length === 0) return;

      const contact = this.contactService.getContactByID(contactConsent.indskr_contactid);
      if (!contact) return;
      // Align with "MY_CUSTOMERS"
      if (contact?.isguest || !contact?.isActive || contact?.indskr_iseventparticipant == true) return;

      // Collect all expiring terms for this contact
      contactConsent.activeConsents.forEach(term => {
        if (!term.indskr_untildate) return;

        const untilDate = this.parseUntilDate(term.indskr_untildate);
        if (!untilDate || isNaN(untilDate)) return;
        
        if (untilDate >= now && untilDate <= thirtyDaysTimestamp) {
          // Calculate days left as a calendar-day difference (midnight-to-midnight) to avoid time-of-day off-by-one.
          // Example: Jan 12 -> Jan 30 should show 18 days, regardless of the current time during Jan 12.
          const untilDateObj = new Date(untilDate);
          const nowDateObj = new Date(now);
          const untilStartOfDay = new Date(untilDateObj.getFullYear(), untilDateObj.getMonth(), untilDateObj.getDate()).getTime();
          const nowStartOfDay = new Date(nowDateObj.getFullYear(), nowDateObj.getMonth(), nowDateObj.getDate()).getTime();
          const daysLeft = Math.max(0, Math.round((untilStartOfDay - nowStartOfDay) / MILLISECONDS_PER_DAY));
          // Use createdon to determine latest suggestion - if multiple terms exist, use the most recent one
          const createdTimestamp = term.createdon || term.modifiedon || untilDate;
          
          const contactSuggestions = contactSuggestionsMap.get(contact.ID) || [];
          contactSuggestions.push({
            contactId: contact.ID,
            contact,
            name: contact.fullname || '',
            type: 'Expiring Consent' as const,
            description: this.translate.instant('SPOTLIGHT_CONSENT_EXPIRING', { days: daysLeft }),
            daysLeft,
            timestamp: createdTimestamp // Use createdOn to determine latest suggestion
          });
          contactSuggestionsMap.set(contact.ID, contactSuggestions);
        }
      });
    });

    // For each contact, keep only the latest suggestion (based on createdOn timestamp)
    contactSuggestionsMap.forEach((contactSuggestions) => {
      // Sort by createdOn timestamp (latest first), then take the first one
      contactSuggestions.sort((a, b) => b.timestamp - a.timestamp);
      suggestions.push(contactSuggestions[0]);
    });

    return suggestions;
  }

  private parseUntilDate(untilDate: string | number | null | undefined): number | null {
    if (untilDate === null || untilDate === undefined) return null;
    if (typeof untilDate === 'number') return isNaN(untilDate) ? null : untilDate;
    if (typeof untilDate === 'string') {
      // Numeric strings (e.g. "1750291200000") must be parsed with parseFloat, not new Date(),
      // because new Date("1750291200000") returns Invalid Date in most runtimes.
      const numericValue = parseFloat(untilDate);
      if (!isNaN(numericValue) && numericValue > TIMESTAMP_THRESHOLD) return numericValue;
      const dateValue = new Date(untilDate).getTime();
      return isNaN(dateValue) ? null : dateValue;
    }
    return null;
  }
}

